import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Compact percent format — null-safe.
function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function StatTile({ label, value, accent, testid, helper }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#0E0E0E] border border-[#2A2A2A] p-4 hover:border-[#FF1E1E]/40 transition-colors"
    >
      <div className="text-[10px] font-mono-data tracking-widest text-white/40 uppercase mb-2">
        {label}
      </div>
      <div className={`font-display text-2xl leading-none font-extrabold ${accent ? "text-[#FF1E1E]" : "text-white"}`}>
        {value}
      </div>
      {helper && (
        <div className="text-[10px] font-mono-data text-white/35 mt-2 tracking-wide">
          {helper}
        </div>
      )}
    </div>
  );
}

export default function DataQuality() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/admin/analytics/data-quality")
      .then((r) => !cancelled && setData(r.data))
      .catch(() => toast.error("Failed to load data quality stats"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Merge ratio surfaces how aggressive the normalization was.
  const mergeRatio = data && data.raw_model_entries
    ? (data.duplicates_merged / data.raw_model_entries) * 100
    : 0;

  return (
    <section className="mb-8" data-testid="data-quality">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="qd-overline">Data Quality</div>
          <div className="text-[11px] text-white/45 mt-1">
            Lead-entry hygiene · raw entries vs canonical models after alias normalization.
          </div>
        </div>
        {data?.alias_count !== undefined && (
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/40">
            {data.alias_count} aliases loaded
          </div>
        )}
      </div>

      {loading && !data ? (
        <div className="qd-card p-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
          Loading data quality…
        </div>
      ) : (
        <>
          {/* Three primary stats — Raw / Normalized / Merged */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2A2A2A] mb-5">
            <StatTile
              label="Raw Model Entries"
              value={data?.raw_model_entries ?? "—"}
              testid="dq-raw"
              helper="distinct (make, model) pairs in DB"
            />
            <StatTile
              label="Normalized Models"
              value={data?.normalized_models ?? "—"}
              accent
              testid="dq-normalized"
              helper="canonical models after alias merge"
            />
            <StatTile
              label="Duplicates Merged"
              value={data?.duplicates_merged ?? "—"}
              testid="dq-merged"
              helper={data?.raw_model_entries ? `${fmtPct(mergeRatio)} of raw entries` : ""}
            />
            <StatTile
              label="Unknown / Blank"
              value={data?.unknown_count ?? "—"}
              testid="dq-unknown"
              helper="leads with no resolvable make/model"
            />
          </div>

          {/* Top collapses table — diagnostic. Hidden if there's nothing to merge. */}
          {data?.top_collapses && data.top_collapses.length > 0 && (
            <div className="qd-card p-0 overflow-hidden" data-testid="dq-collapses">
              <div className="px-5 py-4 border-b border-[#1A1A1A]">
                <div className="qd-overline">Top Collapses</div>
                <div className="text-[11px] text-white/45 mt-1">
                  Canonical models that absorbed the most raw variations — the worst lead-entry offenders.
                </div>
              </div>
              <table className="w-full text-xs font-mono-data" data-testid="dq-collapses-table">
                <thead>
                  <tr className="text-[10px] tracking-widest uppercase text-white/40 border-b border-[#1A1A1A]">
                    <th className="px-4 py-2 text-left">Canonical Model</th>
                    <th className="px-4 py-2 text-right">Variations</th>
                    <th className="px-4 py-2 text-left">Raw Entries Merged</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_collapses.map((c, i) => (
                    <tr
                      key={c.canonical}
                      className="border-b border-[#141414]"
                      data-testid={`dq-collapse-row-${i}`}
                    >
                      <td className="px-4 py-2 text-white font-display normal-case">
                        {c.canonical}
                      </td>
                      <td className="px-4 py-2 text-right text-[#FF1E1E] font-bold">
                        {c.merged_pairs}
                      </td>
                      <td className="px-4 py-2 text-white/60 break-all">
                        {c.raw_variations
                          .map((rv) => {
                            const [m, mo] = rv.split("|");
                            return `${m || "(blank)"} / ${mo || "(blank)"}`;
                          })
                          .join("  ·  ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
